import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CalendarCheck, AlertTriangle, Lock, CheckCircle, Download, Camera, FileText, LogOut, FileBarChart, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { compressImageDataUrl } from '../lib/imageUtils';
import { MobileAttendanceCalendar } from '../components/MobileAttendanceCalendar';
import { ResponsiveActions } from '../components/ResponsiveActions';

function CalendarHeatmap({ records, month, employees, currentUserId }) {
  const year = parseInt(month.split('-')[0]);
  const mon = parseInt(month.split('-')[1]);
  const daysInMonth = new Date(year, mon, 0).getDate();
  // Compute "today" in IST (UTC+5:30) so users in India don't see past days
  // as "Upcoming" around midnight UTC. The backend stamps dates in IST.
  const istOffsetMs = 5.5 * 60 * 60 * 1000;
  const today = new Date(Date.now() + istOffsetMs).toISOString().slice(0, 10);
  const isCurrentMonth = today.startsWith(month);

  // Group records by employee — pick the STRONGEST status per (emp, date)
  // so a duplicate (auto-absent + punch-in) row can never mask a real
  // Present. Mirrors the backend's authoritative resolution.
  const STATUS_RANK = { present: 5, late: 4, half_day: 3, absent: 2 };
  const empMap = {};
  records.forEach(r => {
    if (!empMap[r.employee_id]) empMap[r.employee_id] = {};
    const prev = empMap[r.employee_id][r.date];
    const prevRank = STATUS_RANK[prev] || 1;
    const curRank = STATUS_RANK[r.status] || 1;
    if (!prev || curRank > prevRank) empMap[r.employee_id][r.date] = r.status;
  });

  const empList = employees.length > 0
    ? employees
    : [{ id: currentUserId, name: 'You' }];

  const getColor = (status) => {
    if (status === 'present') return 'bg-emerald-500';
    if (status === 'late') return 'bg-yellow-400';
    if (status === 'half_day') return 'bg-orange-400';
    // Everything else that isn't Present/Late/Half Day is simply "Absent"
    // — this includes any legacy `on_leave` rows the backend may still have.
    if (status) return 'bg-red-500';
    return 'bg-neutral-100';
  };

  // Today-only split — matches the selected month only when `today` falls
  // inside it; otherwise we report the last day of the selected month.
  const splitDate = isCurrentMonth ? today : `${month}-${String(daysInMonth).padStart(2, '0')}`;
  const daySplit = { present: 0, half_day: 0, absent: 0, late: 0, not_marked: 0 };
  empList.forEach(emp => {
    const s = empMap[emp.id]?.[splitDate];
    if (s === 'present') daySplit.present += 1;
    else if (s === 'late') { daySplit.present += 1; daySplit.late += 1; }
    else if (s === 'half_day') daySplit.half_day += 1;
    else if (s) daySplit.absent += 1;   // any other recorded status = absent
    else daySplit.not_marked += 1;
  });

  // Per-employee monthly totals — drives the side panel. For each employee
  // tally Present (incl. Late), Half Day, Absent (recorded OR past-day
  // missing), Late counted separately. Future days are excluded.
  const empStats = empList.map(emp => {
    let present = 0, half = 0, absent = 0, late = 0, total = 0;
    for (let i = 1; i <= daysInMonth; i++) {
      const dayStr = `${month}-${String(i).padStart(2, '0')}`;
      if (dayStr > today) continue;  // skip future days
      total += 1;
      const s = empMap[emp.id]?.[dayStr];
      if (s === 'present') present += 1;
      else if (s === 'late') { present += 1; late += 1; }
      else if (s === 'half_day') half += 1;
      else if (s) absent += 1;
      else absent += 1;  // past day, no record → counted as absent
    }
    return { ...emp, present, half, absent, late, total };
  });

  return (
    <div className="attendance-calendar-shell space-y-4" data-testid="attendance-calendar-heatmap">
      {/* Month grid header */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-neutral-500">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-emerald-500" /> Present</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-red-500" /> Absent</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-yellow-400" /> Late</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-orange-400" /> Half Day</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-neutral-50 border border-neutral-100" /> Upcoming</div>
      </div>

      <MobileAttendanceCalendar employees={empList} records={records} month={month} empMap={empMap} today={today} currentUserId={currentUserId} />
      <div className="desktop-attendance-view flex-col gap-4">
        {/* Calendar grid (scrollable on small screens) */}
        <div className="flex-1 min-w-0 overflow-x-auto">
          <div className="min-w-[700px]">
            {/* Day headers */}
            <div className="grid gap-0.5 mb-2" style={{ gridTemplateColumns: `160px repeat(${daysInMonth}, 1fr)` }}>
              <div className="text-xs font-bold text-neutral-500 uppercase tracking-wider p-1">Employee</div>
              {Array.from({ length: daysInMonth }, (_, i) => {
                const dayStr = `${month}-${String(i + 1).padStart(2, '0')}`;
                const isToday = dayStr === today;
                return (
                  <div key={i} className={`text-center text-[9px] font-semibold p-0.5 rounded-sm
                    ${isToday ? 'bg-red-600 text-white' : 'text-neutral-500'}`}>
                    {i + 1}
                  </div>
                );
              })}
            </div>

            {/* Employee rows */}
            {empList.map(emp => (
              <div key={emp.id} className="grid gap-0.5 mb-0.5" style={{ gridTemplateColumns: `160px repeat(${daysInMonth}, 1fr)` }}>
                <div className="text-xs font-medium text-neutral-700 p-1 truncate">{emp.name}</div>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const dayStr = `${month}-${String(i + 1).padStart(2, '0')}`;
                  const status = empMap[emp.id]?.[dayStr];
                  const isPast = dayStr < today;
                  const isFuture = dayStr > today;
                  // Past-day without a record → visually absent; today/future with
                  // no record → Upcoming / neutral (never "absent" by default).
                  const effectiveStatus = status || (isPast ? 'absent' : null);
                  const label = effectiveStatus || 'Upcoming';
                  return (
                    <div key={i} title={`${dayStr}: ${label}`}
                      className={`h-6 rounded-sm transition-colors duration-150 cursor-default
                        ${effectiveStatus ? getColor(effectiveStatus) : 'bg-neutral-50 border border-neutral-100'}
                        ${isFuture ? 'opacity-40' : 'hover:opacity-80'}`} />
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Per-Employee Summary side panel */}
        <div className="lg:w-80 lg:flex-shrink-0" data-testid="attendance-employee-summary">
          <div className="border border-neutral-200 rounded-sm bg-white sticky lg:top-2">
            <div className="px-3 py-2 border-b border-neutral-100 bg-neutral-50">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Per-Employee Totals</p>
              <p className="text-[10px] text-neutral-400 mt-0.5">Month-to-date · {empList.length} employee{empList.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="max-h-[480px] overflow-y-auto divide-y divide-neutral-100">
              {empStats.map(s => (
                <div key={s.id} className="px-3 py-2 hover:bg-neutral-50">
                  <p className="text-xs font-medium text-neutral-900 truncate">{s.name}</p>
                  <div className="grid grid-cols-4 gap-1 mt-1.5 text-center">
                    <div className="bg-emerald-50 rounded-sm py-1" title="Present (incl. Late days)">
                      <p className="text-[9px] font-semibold text-emerald-700 uppercase tracking-wide">Pres</p>
                      <p className="text-xs font-bold text-emerald-700 tabular-nums">{s.present}</p>
                    </div>
                    <div className="bg-orange-50 rounded-sm py-1" title="Half Day">
                      <p className="text-[9px] font-semibold text-orange-700 uppercase tracking-wide">Half</p>
                      <p className="text-xs font-bold text-orange-700 tabular-nums">{s.half}</p>
                    </div>
                    <div className="bg-red-50 rounded-sm py-1" title="Absent (recorded or past-day no record)">
                      <p className="text-[9px] font-semibold text-red-700 uppercase tracking-wide">Abs</p>
                      <p className="text-xs font-bold text-red-700 tabular-nums">{s.absent}</p>
                    </div>
                    <div className="bg-yellow-50 rounded-sm py-1" title="Late punch-ins (subset of Present)">
                      <p className="text-[9px] font-semibold text-yellow-700 uppercase tracking-wide">Late</p>
                      <p className="text-xs font-bold text-yellow-700 tabular-nums">{s.late}</p>
                    </div>
                  </div>
                </div>
              ))}
              {empStats.length === 0 && (
                <p className="px-3 py-6 text-center text-xs text-neutral-400">No employees in current scope.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Daily split for TODAY (or last day of selected month) */}
      <div className="flex flex-wrap gap-4 text-xs text-neutral-600 pt-2 border-t border-neutral-100" data-testid="attendance-day-split">
        <span className="font-semibold text-neutral-700">
          {isCurrentMonth ? "Today's Split" : `${splitDate} Split`} ·
        </span>
        <span>Employees: <strong className="text-neutral-900">{empList.length}</strong></span>
        <span>Present: <strong className="text-emerald-600">{daySplit.present}</strong></span>
        <span>Half Day: <strong className="text-orange-600">{daySplit.half_day}</strong></span>
        <span>Absent: <strong className="text-red-600">{daySplit.absent}</strong></span>
        <span>Late: <strong className="text-yellow-600">{daySplit.late}</strong></span>
        <span>Not Marked: <strong className="text-neutral-500">{daySplit.not_marked}</strong></span>
      </div>
    </div>
  );
}

export default function AttendancePage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // Click-through from Dashboard stat cards: /attendance?date=today&status=present
  const urlStatus = searchParams.get('status') || '';
  const urlDate = searchParams.get('date') || '';
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [photoData, setPhotoData] = useState(null);
  const [branchSettings, setBranchSettings] = useState(null);
  const [overrideDialog, setOverrideDialog] = useState({ open: false, record: null, status: '', in_time: '', out_time: '', reason: '' });
  const [saOverrideDialog, setSAOverrideDialog] = useState({ open: false, mode: 'single', employee_id: '', date: '', start_date: '', end_date: '', status: 'present', in_time: '', out_time: '', reason: '' });
  const [activeTab, setActiveTab] = useState(urlStatus || urlDate ? 'records' : 'calendar');
  const [filterStatus, setFilterStatus] = useState(urlStatus);
  const [filterToday, setFilterToday] = useState(urlDate === 'today');

  // IST today — matches what the heatmap uses so click-through highlights
  // the right row even if the server/user clocks span midnight.
  const todayIst = useMemo(() => {
    const istOffsetMs = 5.5 * 60 * 60 * 1000;
    return new Date(Date.now() + istOffsetMs).toISOString().slice(0, 10);
  }, []);

  const isManager = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role);
  const isSuperAdmin = user?.system_role === 'super_admin';

  // Apply Dashboard-driven filters over `records` + `employees`. For "Absent Today"
  // we synthesize virtual rows per employee who has no present/late/half_day
  // record today (i.e. implicit absentees), since the DB usually only stores
  // positive punches. All other statuses come straight from the records.
  const displayRecords = useMemo(() => {
    let list = records;
    if (filterToday) list = list.filter(r => r.date === todayIst);
    if (filterStatus === 'present') {
      list = list.filter(r => r.status === 'present' || r.status === 'late');
    } else if (filterStatus === 'half_day' || filterStatus === 'late') {
      list = list.filter(r => r.status === filterStatus);
    } else if (filterStatus === 'absent' && filterToday) {
      // Implicit absentees — active employees with no positive punch today.
      const markedIds = new Set(records
        .filter(r => r.date === todayIst && ['present', 'late', 'half_day'].includes(r.status))
        .map(r => r.employee_id));
      const explicit = records.filter(r => r.date === todayIst && r.status === 'absent');
      const explicitIds = new Set(explicit.map(r => r.employee_id));
      const implicit = employees
        .filter(e => !markedIds.has(e.id) && !explicitIds.has(e.id))
        .map(e => ({
          id: `virtual-absent-${e.id}`,
          employee_id: e.id,
          date: todayIst,
          status: 'absent',
          in_time: null,
          out_time: null,
          is_virtual: true,
        }));
      list = [...explicit, ...implicit];
    } else if (filterStatus === 'absent') {
      list = list.filter(r => r.status === 'absent');
    }
    return list;
  }, [records, employees, filterToday, filterStatus, todayIst]);

  const fetchData = useCallback(async () => {
    try {
      const params = { month: selectedMonth };
      if (selectedBranch && selectedBranch !== 'all') params.branch_id = selectedBranch;
      // Branch filter must apply to BOTH attendance records AND the employee
      // roster shown in the calendar grid — otherwise a SA picking "Branch X"
      // sees X's punches scattered across the global employee list.
      const empParams = (selectedBranch && selectedBranch !== 'all') ? { branch_id: selectedBranch } : {};
      const [attRes, empRes, brRes] = await Promise.all([
        api.get('/attendance', { params }),
        isManager ? api.get('/employees', { params: empParams }) : Promise.resolve({ data: [] }),
        api.get('/branches')
      ]);
      setRecords(attRes.data);
      setEmployees(empRes.data);
      setBranches(brRes.data);
      // Check eligibility for current user (sales staff)
      const eligRes = await api.get(`/attendance/eligibility/${user.id}`);
      setEligibility(eligRes.data);
      if (user.branch_id) {
        const sRes = await api.get(`/branch-settings/${user.branch_id}`);
        setBranchSettings(sRes.data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedMonth, selectedBranch, isManager, user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const markAttendance = async (employeeId = null) => {
    try {
      // Photo is MANDATORY for every role except Super Admin. Management is
      // now included in the photo requirement (parity with all other staff).
      const isSuperAdmin = user?.system_role === 'super_admin';
      if (!employeeId && !isSuperAdmin && !photoData) {
        toast.error('Please capture your selfie before punching in.');
        return;
      }
      const today = new Date().toISOString().slice(0, 10);
      const payload = { employee_id: employeeId || undefined, date: today, status: 'present' };
      if (!employeeId && photoData) {
        payload.photo_base64 = photoData;
      }
      await api.post('/attendance/mark', payload);
      toast.success('Attendance marked successfully');
      setPhotoData(null);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      toast.error('No photo captured. Please try again.');
      return;
    }
    try {
      const compressed = await compressImageDataUrl(file);
      // Guard against iOS canvas failure returning "data:image/jpeg;base64," with no payload.
      if (!compressed || compressed.length < 100) throw new Error('empty result');
      setPhotoData(compressed);
    } catch {
      // Fallback: skip client-side compression (some iOS builds return HEIC
      // that our canvas can't decode). Use the raw FileReader base64 so the
      // punch-in / punch-out still succeeds.
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) setPhotoData(reader.result);
        else toast.error('Could not read photo. Please retake.');
      };
      reader.onerror = () => toast.error('Could not read photo. Please retake.');
      reader.readAsDataURL(file);
    }
    // Reset input so the same file can be re-selected on iOS if the first attempt failed.
    e.target.value = '';
  };

  const markExit = async () => {
    try {
      const isSuperAdmin = user?.system_role === 'super_admin';
      if (!isSuperAdmin && !photoData) {
        toast.error('Please capture your exit selfie before punching out.');
        return;
      }
      await api.post('/attendance/mark-exit', { photo_base64: photoData || null });
      toast.success('Exit time recorded. Have a great evening!');
      setPhotoData(null);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openOverride = (rec) => {
    setOverrideDialog({
      open: true, record: rec,
      status: rec.status || 'present',
      in_time: rec.in_time || '',
      out_time: rec.out_time || '',
      reason: '',
    });
  };

  const handleOverrideSave = async () => {
    const { record, status, in_time, out_time, reason } = overrideDialog;
    if (!record) return;
    try {
      const payload = {};
      if (status && status !== record.status) payload.status = status;
      if (in_time && in_time !== record.in_time) payload.in_time = in_time;
      if (out_time && out_time !== (record.out_time || '')) payload.out_time = out_time;
      if (reason) payload.reason = reason;
      if (Object.keys(payload).length === 0) { toast.error('Nothing to change'); return; }
      await api.put(`/attendance/${record.id}/override`, payload);
      toast.success('Attendance overridden');
      setOverrideDialog({ open: false, record: null, status: '', in_time: '', out_time: '', reason: '' });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Super-Admin: edit attendance for ANY past/future date — single or range.
  const handleSAOverrideSave = async () => {
    const d = saOverrideDialog;
    if (!d.employee_id) { toast.error('Pick an employee'); return; }
    if (!d.status) { toast.error('Pick a status'); return; }
    if (!d.reason || d.reason.trim().length < 3) { toast.error('Audit reason is required'); return; }
    try {
      if (d.mode === 'range') {
        if (!d.start_date || !d.end_date) { toast.error('Pick start & end dates'); return; }
        if (d.start_date > d.end_date) { toast.error('Start must be before end'); return; }
        const r = await api.post('/attendance/admin-range-upsert', {
          employee_id: d.employee_id,
          start_date: d.start_date,
          end_date: d.end_date,
          status: d.status,
          in_time: d.in_time || null,
          out_time: d.out_time || null,
          reason: d.reason,
        });
        toast.success(`Updated ${r.data.dates_affected} day(s) for ${r.data.employee_name}`);
      } else {
        if (!d.date) { toast.error('Pick a date'); return; }
        await api.post('/attendance/admin-upsert', {
          employee_id: d.employee_id,
          date: d.date,
          status: d.status,
          in_time: d.in_time || null,
          out_time: d.out_time || null,
          reason: d.reason,
        });
        toast.success('Attendance saved');
      }
      setSAOverrideDialog(p => ({ ...p, open: false }));
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const closeDaySweep = async (date) => {
    if (!window.confirm(`Auto-close ${date}? All employees without punch-out will be marked ON LEAVE.`)) return;
    try {
      const r = await api.post(`/attendance/close-day?date=${date}`, {});
      toast.success(`Closed ${date} — ${r.data.swept} record(s) marked leave`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const getEmpName = (eid) => {
    if (eid === user?.id) return user?.name;
    return employees.find(e => e.id === eid)?.name || eid;
  };

  const handleExportCSV = () => {
    const token = localStorage.getItem('hrms_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    let url = `${base}/api/export/attendance?month=${selectedMonth}`;
    if (selectedBranch && selectedBranch !== 'all') url += `&branch_id=${selectedBranch}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `attendance_${selectedMonth}.csv`;
        a.click();
      });
  };

  const handleGeneratePDF = () => {
    const token = localStorage.getItem('hrms_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    const today = new Date().toISOString().slice(0, 10);
    toast.info('Generating PDF...');
    fetch(`${base}/api/attendance/summary-pdf?date=${today}&_=${Date.now()}`, { cache: 'no-store', headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `attendance_${today}.pdf`;
        a.click();
        toast.success('PDF downloaded');
      })
      .catch(() => toast.error('Failed to generate PDF'));
  };

  const handleMonthlyReport = () => {
    const token = localStorage.getItem('hrms_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    toast.info('Generating monthly report (may take a few seconds)...');
    fetch(`${base}/api/attendance/monthly-report-pdf?month=${selectedMonth}&_=${Date.now()}`, { cache: 'no-store', headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `attendance_monthly_${selectedMonth}.pdf`;
        a.click();
        toast.success('Monthly report downloaded');
      })
      .catch(() => toast.error('Failed to generate monthly report'));
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  // Part-time employees may have TWO rows per day (morning + afternoon).
  // Order by in_time so we look at the latest session for punch-in/out state.
  const myTodaySessions = records
    .filter(r => r.employee_id === user?.id && r.date === todayStr)
    .sort((a, b) => (a.in_time || '').localeCompare(b.in_time || ''));
  const myTodayRecord = myTodaySessions[myTodaySessions.length - 1] || null;
  const isPartTime = user?.job_role === 'Part Time';
  // Part-time flow: after fully completing a session (in + out), a fresh
  // Punch-In should reappear for the afternoon session.
  const partTimeNeedsSecondPunchIn = isPartTime && myTodayRecord && myTodayRecord.in_time && myTodayRecord.out_time && myTodaySessions.length < 2;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Attendance</h1>
          <p className="text-neutral-500 text-sm mt-1">Track and manage daily attendance</p>
        </div>
        {isManager && (
          <ResponsiveActions testId="attendance-report-actions">
            <Button variant="outline" className="rounded-sm" onClick={handleExportCSV} data-testid="export-attendance-button">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button variant="outline" className="rounded-sm border-red-200 text-red-700 hover:bg-red-50" onClick={handleGeneratePDF} data-testid="generate-attendance-pdf-button">
              <FileText className="h-4 w-4 mr-2" /> Daily Summary (PDF)
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={handleMonthlyReport} data-testid="generate-monthly-report-button">
              <FileBarChart className="h-4 w-4 mr-2" /> Monthly Report (PDF)
            </Button>
            {isSuperAdmin && (
              <Button variant="outline" className="rounded-sm border-red-200 text-red-700 hover:bg-red-50"
                onClick={() => setSAOverrideDialog({ open: true, mode: 'single', employee_id: '', date: new Date().toISOString().slice(0,10), start_date: '', end_date: '', status: 'present', in_time: '', out_time: '', reason: '' })}
                data-testid="sa-override-attendance-btn">
                <Pencil className="h-4 w-4 mr-2" /> Edit Any Date
              </Button>
            )}
          </ResponsiveActions>
        )}
      </div>

      {/* Mark Attendance Card - All staff */}
      <Card className="border border-neutral-200 shadow-sm rounded-md">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-neutral-800">My Attendance Today</h3>
                {myTodayRecord ? (
                  <div className="mt-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-green-700">Marked <strong>{myTodayRecord.status}</strong>{myTodayRecord.in_time ? ` at ${myTodayRecord.in_time}` : ''}</span>
                    </div>
                    {myTodayRecord.out_time ? (
                      <div className="flex items-center gap-2">
                        <LogOut className="h-4 w-4 text-blue-500" />
                        <span className="text-sm text-blue-700">Exit marked at <strong>{myTodayRecord.out_time}</strong></span>
                      </div>
                    ) : (
                      <p className="text-xs text-neutral-500">Don&apos;t forget to mark your exit when you leave.</p>
                    )}
                  </div>
                ) : eligibility && !eligibility.eligible ? (
                  <div className="flex items-start gap-2 mt-1" data-testid="attendance-blocked-msg">
                    <Lock className="h-4 w-4 text-red-500 mt-0.5" />
                    <span className="text-sm text-red-700">{eligibility.message}</span>
                  </div>
                ) : eligibility?.warning ? (
                  <div className="flex items-start gap-2 mt-1" data-testid="attendance-warning-msg">
                    <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                    <span className="text-sm text-amber-700">{eligibility.message}</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-neutral-500">Mark your attendance for today</p>
                    {branchSettings?.attendance_cutoff && (
                      <p className="text-xs text-neutral-400">Cutoff: {branchSettings.attendance_cutoff}</p>
                    )}
                  </div>
                )}
              </div>
              {(!myTodayRecord || partTimeNeedsSecondPunchIn) && (eligibility?.eligible !== false) && (() => {
                const needsPhoto = user?.system_role !== 'super_admin';
                return (
                  <div className="attendance-punch-actions flex flex-col sm:flex-row items-start sm:items-center gap-2">
                    {needsPhoto && (
                      <>
                        <label
                          htmlFor="attendance-photo-input"
                          className={`flex items-center gap-2 px-4 py-2 border-2 rounded-sm cursor-pointer text-sm font-semibold transition-all select-none ${photoData ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-red-400 bg-red-50 text-red-700 hover:bg-red-100 animate-pulse'}`}
                          data-testid="attendance-photo-label">
                          <Camera className={`h-5 w-5 ${photoData ? 'text-emerald-600' : 'text-red-600'}`} />
                          {photoData ? <span className="text-xs">✓ Selfie ready</span> : <span className="text-xs">Tap to take Selfie *</span>}
                        </label>
                        {/*
                          File input MUST remain a real, focusable element in the
                          document — using Tailwind `hidden` (display:none) or
                          `sr-only` (1×1px clip) breaks iOS Safari/PWA taps: the
                          browser silently strips user-activation and the camera
                          never opens. Absolute + opacity:0 keeps the input
                          layout-valid, the <label htmlFor> forwards the tap
                          natively as a genuine user gesture, and the camera
                          opens reliably across iOS, Android, and desktop.
                        */}
                        <input
                          id="attendance-photo-input"
                          type="file"
                          accept="image/*"
                          capture="user"
                          onChange={handlePhotoCapture}
                          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
                          data-testid="attendance-photo-input"
                        />
                      </>
                    )}
                    <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={() => markAttendance()}
                      disabled={needsPhoto && !photoData}
                      data-testid="mark-attendance-button">
                      <CalendarCheck className="h-4 w-4 mr-2" /> {partTimeNeedsSecondPunchIn ? 'Punch In (Afternoon)' : 'Punch In'}
                    </Button>
                  </div>
                );
              })()}
              {myTodayRecord && !myTodayRecord.out_time && (() => {
                const needsPhoto = user?.system_role !== 'super_admin';
                return (
                  <div className="attendance-punch-actions flex flex-col sm:flex-row items-start sm:items-center gap-2">
                    {needsPhoto && (
                      <>
                        <label
                          htmlFor="exit-photo-input"
                          className={`flex items-center gap-2 px-4 py-2 border-2 rounded-sm cursor-pointer text-sm font-semibold transition-all select-none ${photoData ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-red-400 bg-red-50 text-red-700 hover:bg-red-100 animate-pulse'}`}
                          data-testid="exit-photo-label">
                          <Camera className={`h-5 w-5 ${photoData ? 'text-emerald-600' : 'text-red-600'}`} />
                          {photoData ? <span className="text-xs">✓ Selfie ready</span> : <span className="text-xs">Tap to take Exit Selfie *</span>}
                        </label>
                        <input
                          id="exit-photo-input"
                          type="file"
                          accept="image/*"
                          capture="user"
                          onChange={handlePhotoCapture}
                          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
                          data-testid="exit-photo-input"
                        />
                      </>
                    )}
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm" onClick={markExit}
                      disabled={needsPhoto && !photoData}
                      data-testid="mark-exit-button">
                      <LogOut className="h-4 w-4 mr-2" /> Punch Out
                    </Button>
                  </div>
                );
              })()}
            </div>
          </CardContent>
        </Card>

      {photoData && <div className="flex items-center gap-3" data-testid="attendance-selfie-review">
        <img src={photoData} alt="Selected attendance selfie" className="w-24 aspect-square object-contain bg-neutral-100 rounded-md border border-neutral-200" data-testid="attendance-selfie-preview" />
        <Button variant="outline" onClick={() => setPhotoData(null)} data-testid="attendance-selfie-retake"><Camera className="h-4 w-4" /> Retake selfie</Button>
      </div>}

      {/* Manager: Mark for others — Super Admin only.
          Management & Branch Manager are intentionally excluded. */}
      {isSuperAdmin && (
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-neutral-700">Mark Attendance (Admin Override)</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={selectedEmpId} onValueChange={setSelectedEmpId}>
                <SelectTrigger className="w-full sm:w-64 rounded-sm" data-testid="attendance-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" disabled={!selectedEmpId}
                onClick={() => markAttendance(selectedEmpId)} data-testid="mark-attendance-override-button">
                <CalendarCheck className="h-4 w-4 mr-2" /> Mark Present
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
          className="h-10 px-3 border border-neutral-200 rounded-sm text-sm bg-white" data-testid="attendance-month-filter" />
        {isManager && user?.system_role !== 'branch_manager' && (
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="w-full sm:w-48 rounded-sm" data-testid="attendance-branch-filter">
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Tabs: Calendar + Records */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="calendar" className="rounded-sm" data-testid="attendance-calendar-tab">Calendar View</TabsTrigger>
          <TabsTrigger value="records" className="rounded-sm" data-testid="attendance-records-tab">Records</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-4">
          <Card className="border border-neutral-200 shadow-sm rounded-md">
            <CardContent className="p-4 sm:p-6">
              {loading ? (
                <div className="h-40 bg-neutral-100 rounded animate-pulse" />
              ) : (
                <CalendarHeatmap
                  records={records}
                  month={selectedMonth}
                  employees={employees}
                  currentUserId={user?.id}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="records" className="mt-4 space-y-3">
          {/* Filter chips (active when coming from Dashboard click-through) */}
          {(filterToday || filterStatus) && (
            <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="records-filter-chips">
              <span className="text-neutral-500 font-semibold">Filters:</span>
              {filterToday && (
                <Badge className="bg-red-100 text-red-700 border border-red-200">
                  Date: Today ({todayIst})
                  <button className="ml-1.5 text-red-700/70 hover:text-red-700" onClick={() => setFilterToday(false)} data-testid="clear-filter-today">×</button>
                </Badge>
              )}
              {filterStatus && (
                <Badge className="bg-blue-100 text-blue-700 border border-blue-200 capitalize">
                  Status: {filterStatus.replace('_', ' ')}
                  <button className="ml-1.5 text-blue-700/70 hover:text-blue-700" onClick={() => setFilterStatus('')} data-testid="clear-filter-status">×</button>
                </Badge>
              )}
              <button className="text-neutral-500 hover:text-neutral-800 underline underline-offset-2" onClick={() => { setFilterToday(false); setFilterStatus(''); }} data-testid="clear-all-filters">
                Clear all
              </button>
            </div>
          )}
          <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Date</TableHead>
                    {isManager && <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Employee</TableHead>}
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Status</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden md:table-cell">In / Out</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden sm:table-cell">Flag</TableHead>
                    {isSuperAdmin && <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}><TableCell colSpan={6}><div className="h-8 bg-neutral-100 rounded animate-pulse" /></TableCell></TableRow>
                    ))
                  ) : displayRecords.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-12 text-neutral-500">No records found</TableCell></TableRow>
                  ) : displayRecords.map(r => (
                    <TableRow key={r.id} className={`hover:bg-neutral-50 ${r.is_virtual ? 'bg-red-50/40' : ''}`}>
                      <TableCell className="text-sm text-neutral-900 font-medium">{r.date}</TableCell>
                      {isManager && <TableCell className="text-sm text-neutral-800">{getEmpName(r.employee_id)}</TableCell>}
                      <TableCell>
                        <Badge className={`text-xs capitalize ${r.status === 'present' ? 'bg-green-100 text-green-700' : r.status === 'absent' ? 'bg-red-100 text-red-700' : r.status === 'on_leave' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                          {r.status?.replace('_', ' ')}
                          {r.is_virtual && ' (not marked)'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-neutral-800 hidden md:table-cell tabular-nums">
                        {r.in_time || '—'} / {r.out_time || <span className="text-red-600">missing</span>}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {r.is_override && <Badge variant="secondary" className="text-[10px] mr-1">Override</Badge>}
                        {r.late_flag && <Badge className="text-[10px] bg-yellow-100 text-yellow-700 border border-yellow-200">Late</Badge>}
                        {!r.is_override && !r.late_flag && !r.is_virtual && <span className="text-xs text-neutral-400">—</span>}
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell className="text-right">
                          {!r.is_virtual && (
                            <Button variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => openOverride(r)} data-testid={`override-att-${r.id}`} title="Override">
                              <Pencil className="h-3.5 w-3.5 text-neutral-700" />
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {isSuperAdmin && (
              <div className="p-3 border-t border-neutral-200 bg-neutral-50 flex justify-end">
                <Button variant="outline" size="sm" className="text-xs rounded-sm"
                  onClick={() => closeDaySweep(new Date().toISOString().slice(0, 10))}
                  data-testid="close-day-btn">
                  <Lock className="h-3.5 w-3.5 mr-1.5" /> Close Today & Mark Missing-Exit as Leave
                </Button>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── SA Override Dialog ── */}
      <Dialog open={overrideDialog.open} onOpenChange={(o) => !o && setOverrideDialog({ open: false, record: null, status: '', in_time: '', out_time: '', reason: '' })}>
        <DialogContent className="max-w-md" data-testid="override-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Override Attendance</DialogTitle>
            <DialogDescription>
              {overrideDialog.record && <>{getEmpName(overrideDialog.record.employee_id)} · {overrideDialog.record.date}</>}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1 col-span-2">
              <Label className="text-xs font-medium">Status</Label>
              <Select value={overrideDialog.status} onValueChange={(v) => setOverrideDialog(p => ({ ...p, status: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="override-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="half_day">Half Day</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium">In Time</Label>
              <Input type="time" value={overrideDialog.in_time}
                onChange={e => setOverrideDialog(p => ({ ...p, in_time: e.target.value }))}
                className="rounded-sm" data-testid="override-intime" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium">Out Time</Label>
              <Input type="time" value={overrideDialog.out_time}
                onChange={e => setOverrideDialog(p => ({ ...p, out_time: e.target.value }))}
                className="rounded-sm" data-testid="override-outtime" />
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-xs font-medium">Reason (audit log)</Label>
              <Textarea rows={2} value={overrideDialog.reason}
                onChange={e => setOverrideDialog(p => ({ ...p, reason: e.target.value }))}
                placeholder="e.g. Employee forgot to punch; verified via CCTV"
                className="rounded-sm" data-testid="override-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialog({ open: false, record: null, status: '', in_time: '', out_time: '', reason: '' })} className="rounded-sm">Cancel</Button>
            <Button onClick={handleOverrideSave} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="override-save-btn">Save Override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Super-Admin: Edit Attendance for ANY date / range */}
      <Dialog open={saOverrideDialog.open} onOpenChange={(o) => setSAOverrideDialog(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-lg" data-testid="sa-override-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Edit Attendance — Any Date</DialogTitle>
            <DialogDescription className="text-xs">Super Admin only · all changes are logged in the audit trail.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <button type="button"
                onClick={() => setSAOverrideDialog(p => ({ ...p, mode: 'single' }))}
                className={`px-3 py-2 rounded-sm text-xs font-semibold border ${saOverrideDialog.mode === 'single' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-neutral-700 border-neutral-200'}`}
                data-testid="sa-override-mode-single">Single Day</button>
              <button type="button"
                onClick={() => setSAOverrideDialog(p => ({ ...p, mode: 'range' }))}
                className={`px-3 py-2 rounded-sm text-xs font-semibold border ${saOverrideDialog.mode === 'range' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-neutral-700 border-neutral-200'}`}
                data-testid="sa-override-mode-range">Date Range</button>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium">Employee</Label>
              <Select value={saOverrideDialog.employee_id}
                onValueChange={v => setSAOverrideDialog(p => ({ ...p, employee_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="sa-override-employee">
                  <SelectValue placeholder="Select employee..." />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name} {e.employee_code ? `· ${e.employee_code}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {saOverrideDialog.mode === 'single' ? (
              <div className="space-y-1">
                <Label className="text-xs font-medium">Date (any past or future)</Label>
                <Input type="date" value={saOverrideDialog.date}
                  onChange={e => setSAOverrideDialog(p => ({ ...p, date: e.target.value }))}
                  className="rounded-sm" data-testid="sa-override-date" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Start</Label>
                  <Input type="date" value={saOverrideDialog.start_date}
                    onChange={e => setSAOverrideDialog(p => ({ ...p, start_date: e.target.value }))}
                    className="rounded-sm" data-testid="sa-override-start" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">End</Label>
                  <Input type="date" value={saOverrideDialog.end_date}
                    onChange={e => setSAOverrideDialog(p => ({ ...p, end_date: e.target.value }))}
                    className="rounded-sm" data-testid="sa-override-end" />
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs font-medium">Status</Label>
              <Select value={saOverrideDialog.status}
                onValueChange={v => setSAOverrideDialog(p => ({ ...p, status: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="sa-override-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="half_day">Half Day</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {saOverrideDialog.status !== 'absent' && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">In Time (optional)</Label>
                  <Input type="time" value={saOverrideDialog.in_time}
                    onChange={e => setSAOverrideDialog(p => ({ ...p, in_time: e.target.value }))}
                    className="rounded-sm" data-testid="sa-override-in" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Out Time (optional)</Label>
                  <Input type="time" value={saOverrideDialog.out_time}
                    onChange={e => setSAOverrideDialog(p => ({ ...p, out_time: e.target.value }))}
                    className="rounded-sm" data-testid="sa-override-out" />
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs font-medium">Reason (audit log)</Label>
              <Textarea rows={2} value={saOverrideDialog.reason}
                onChange={e => setSAOverrideDialog(p => ({ ...p, reason: e.target.value }))}
                placeholder="e.g. Manual correction after attendance audit"
                className="rounded-sm" data-testid="sa-override-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSAOverrideDialog(p => ({ ...p, open: false }))} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSAOverrideSave}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
              data-testid="sa-override-save-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
